/* global FormData */
/* global localStorage */
import ErrorMessage from "../../components/ErrorMessage";
import React, { useContext, useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { apiGet, apiPut } from "../../utils/apiFetch";
import pathObj from "../../utils/apiPath";
import useToastContext from "hooks/useToastContext";
import OButton from "components/reusable/OButton";
import OImage from "components/reusable/OImage";
import { useTranslation } from "react-i18next";
import { AiFillCamera } from "react-icons/ai";
import { isEmpty } from "lodash";
import AuthContext from "context/AuthContext";
import OInputField from "components/reusable/OInputField";

const Settings = () => {
  const profilePicRef = useRef();
  const loginPicRef = useRef();
  const { logoutUser, user, setSettingsLogo, setCountry } =
    useContext(AuthContext);
  const manager = user?.permission?.find((e) => e.manager === "settings") ?? {};
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
  } = useForm({
    mode: "onBlur",
    shouldFocusError: true,
    defaultValues: {},
  });
  const [settingChangeLoading, setSettingChangeLoading] = useState(false);
  const [profilePicError, setProfilePicError] = useState("");
  const [profilePic, setProfilePic] = useState(null);
  const [loginPicError, setLoginPicError] = useState("");
  const [loginPic, setLoginPic] = useState(null);
  const [pic, setPic] = useState(null);
  const [banner, setBanner] = useState(null);
  const notification = useToastContext();
  const [isDisable, setIsDisable] = useState(true);

  const handleSubmitForm = async (data) => {
    try {
      const formData = new FormData();
      formData.append("siteName", data.siteName);
      formData.append("siteNameAr", data.siteNameAr);
      formData.append("siteAddress", data.siteAddress);
      formData.append("siteAddressAr", data.siteAddressAr);
      formData.append("siteAddress2", data.siteAddress2);
      formData.append("siteAddressAr2", data.siteAddressAr2);
      formData.append("siteAddress3", data.siteAddress3);
      formData.append("siteAddressAr3", data.siteAddressAr3);
      formData.append("siteAddress4", data.siteAddress4);
      formData.append("siteAddressAr4", data.siteAddressAr4);
      formData.append("slogan", data.slogan);
      formData.append("country", data.country);
      formData.append("sloganAr", data.sloganAr);
      formData.append("email", data.email);
      formData.append("mobile", data.mobile);
      formData.append("googleAnalyticsCode", data.googleAnalyticsCode);
      formData.append("facebookUrl", data.facebookUrl);
      formData.append("twitterUrl", data.twitterUrl);
      formData.append("linkedinUrl", data.linkedinUrl);
      formData.append("instgramUrl", data.instgramUrl);
      formData.append("youtubeUrl", data.youtubeUrl);
      formData.append("iOSVersion", data.iOSVersion);
      formData.append("androidVersion", data.androidVersion);
      formData.append("agentiOSVersion", data.agentiOSVersion);
      formData.append("agentAndroidVersion", data.agentAndroidVersion);
      formData.append("conversion_rate", parseInt(data.conversion_rate));
      if (profilePic !== null) {
        formData.append("logo", profilePic);
      }
      if (loginPic !== null) {
        formData.append("loginBanner", loginPic);
      }

      // data.conversion_rate = parseInt(data.conversion_rate)
      setSettingChangeLoading(true);
      const res = await apiPut(pathObj.updateSettings, formData);
      if (res.data.success === true) {
        getSettings();
        notification.success(res?.data?.message);
      } else {
        notification.error(res?.data?.message);
      }
    } catch (err) {
      console.log("err:", err);
    } finally {
      setSettingChangeLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const image = e?.target?.files[0];
    const isValidImage = validateFiles(image);
    if (isValidImage) {
      setIsDisable(false);
      setProfilePic(image);
    } else {
      setProfilePic(null);
    }
  };
  const handleBannerFileChange = (e) => {
    const image = e?.target?.files[0];
    const isValidImage = validateBannerFiles(image);
    if (isValidImage) {
      setIsDisable(false);
      setLoginPic(image);
    } else {
      setLoginPic(null);
    }
  };

  const validateFiles = (profilePic) => {
    let picError = "";
    const supportedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!isEmpty(supportedTypes.includes(profilePic.type))) {
      picError = "Only jpeg, jpg and png are supported.";
    }

    if (picError) {
      setProfilePicError(picError);
      return false;
    } else {
      setProfilePicError("");
      return true;
    }
  };

  const validateBannerFiles = (profilePic) => {
    let picError = "";
    const supportedTypes = ["image/jpeg", "image/jpg", "image/png"];

    if (!isEmpty(supportedTypes.includes(profilePic.type))) {
      picError = "Only jpeg, jpg and png are supported.";
    }

    if (picError) {
      setLoginPicError(picError);
      return false;
    } else {
      setLoginPicError("");
      return true;
    }
  };

  const getSettings = async () => {
    try {
      const res = await apiGet(pathObj.getSettings);
      if (res) {
        reset(res?.data?.results);
        setPic(res?.data?.results?.logo);
        setBanner(res?.data?.results?.loginBanner);
        setSettingsLogo(res?.data?.results?.logo);
        localStorage.setItem("settingsLogo", res?.data.results?.logo);
        localStorage.setItem("country", res?.data.results?.country);
        setCountry(localStorage.getItem("country"));
      }
    } catch (error) {
      console.log("error:", error);

      if (error.response.status === 401 || error.response.status === 409) {
        logoutUser();
      }
    }
  };
  useEffect(() => {
    getSettings();
  }, []);

  return (
    <section className="">
      <form onSubmit={handleSubmit(handleSubmitForm)} method="post">
        <section className="sm:px-8 px-4 py-4 ">
          <div className="border xl:w-full round">
            <header className="border-b  py-2 px-4 bg-gray-100 rounded-t-md ">
              <div className="font-semibold">{t("WEB_SETTING")}</div>
            </header>
            <div className="bg-white py-6 px-4  rounded-b-md">
              {/* <main className='justify-center flex flex-wrap xl:[&>*]:mr-14 sm:[&>*]:mr-7 2xl:[&>*]:mr-14  sm:px-0 px-4 xl:[&>*]:w-3/12 sm:[&>*]:w-3/5 '> */}
              <main className="justify-center flex flex-wrap grid  lg:grid-cols-3 md:grid-cols-3 sm:grid-cols-2 grid-cols-1  gap-4">
                {/* <div className='mb-4  w-full'>
                  <label className='block text-gray-700 text-sm font-medium mb-2'>
                    {t('O_COUNTRY')}
                    <span className='text-red-500'>*</span>
                  </label>
                  <div className='flex border rounded'>
                    <select
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='text'
                      defaultValue='+91'
                      id='country'
                      {...register('country', {
                        required: {
                          value: true,
                          message: 'Please select base country.'
                        }
                      })}
                      placeholder=' '
                    >
                      <option value=''>{t('SELECT_BASE_COUNTRY')}</option>
                      <option value='UAE'>{t('UAE')}</option>
                      <option value='Saudi'>{t('SAUDI_ARABIA')}</option>
                    </select>
                  </div>
                  <ErrorMessage message={errors?.country?.message} />
                </div> */}
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_NAME")}</>}
                      type="text"
                      id="siteName"
                      register={register("siteName", {
                        required: {
                          value: true,
                          message: "Please enter site name.",
                        },
                        pattern: {
                          value: /^[a-zA-Z ]+$/,
                          message: "Invalid site name.",
                        },
                        maxLength: {
                          value: 30,
                          message: "Max limit is 30 characters.",
                        },
                        min: {
                          value: 1,
                          message: "Minimum value must is 1.",
                        },
                      })}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteName?.message} />
                </div>
                <div className="w-full">
                  {/* <label className='block text-gray-700 text-sm font-medium mb-2'>
                    {t('SITE_NAME') + t('O_ARABIC')}
                  </label> */}
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_NAME") + t("O_ARABIC")}</>}
                      type="text"
                      id="siteNameAr"
                      register={register("siteNameAr", {
                        required: {
                          value: true,
                          message: "Please enter site name in arabic.",
                        },
                        maxLength: {
                          value: 30,
                          message: "Max limit is 30 characters.",
                        },
                        min: {
                          value: 1,
                          message: "Minimum value must is 1.",
                        },
                      })}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteNameAr?.message} />
                </div>

                <div className="w-full">
                  {/* <label className='block text-gray-700 text-sm font-medium mb-2'>
                    {t('O_EMAIL_ADDRESS')}
                  </label> */}
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("O_EMAIL_ADDRESS")}</>}
                      type="text"
                      id="email"
                      // onInput={(e) => preventMaxValue(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      register={register("email", {
                        required: {
                          value: true,
                          message: "Please enter email address.",
                        },
                        pattern: {
                          value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                          message:
                            "Invalid email address (Ex: example@domain.com).",
                        },
                        maxLength: {
                          value: 30,
                          message: "Max limit is 30 characters.",
                        },
                        min: {
                          value: 5,
                          message: "Minimum value must is 5.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.email?.message} />
                </div>
                <div className="w-full  ">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("O_MOBILE")}</>}
                      type="text"
                      // onInput={e => preventMaxValue(e)}
                      // onKeyDown={(event) => {
                      //   if (
                      //     !['Backspace', 'Delete', 'Tab'].includes(event.key) &&
                      //     !/[0-9]/.test(event.key)
                      //   ) {
                      //     event.preventDefault();
                      //   }
                      // }}
                      register={register("mobile", {
                        required: {
                          value: true,
                          message: "Please enter mobile number.",
                        },
                        maxLength: {
                          value: 15,
                          message: "Max limit is 15 digits.",
                        },
                        // pattern: {
                        //   value: /^\d+$/,
                        //   message: 'Only digits are allowed.',
                        // },
                        min: {
                          value: 7,
                          message: "Minimum value must is 7.",
                        },
                      })}
                      placeholder=""
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.mobile?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SLOGAN")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="slogan"
                      register={register("slogan", {
                        required: {
                          value: true,
                          message: "Please enter slogan.",
                        },
                        pattern: {
                          value: /^[a-zA-Z. ]+$/,
                          message: "Invalid slogan.",
                        },
                        maxLength: {
                          value: 50,
                          message: "Max limit is 30 characters.",
                        },
                        min: {
                          value: 1,
                          message: "Minimum value must is 1.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.slogan?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SLOGAN") + t("O_ARABIC")}</>}
                      type="text"
                      id="sloganAr"
                      register={register("sloganAr", {
                        required: {
                          value: true,
                          message: "Please enter slogan in arabic.",
                        },
                        maxLength: {
                          value: 50,
                          message: "Max limit is 30 characters.",
                        },
                        min: {
                          value: 1,
                          message: "Minimum value must is 1.",
                        },
                      })}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.sloganAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="siteAddress"
                      register={register("siteAddress", {
                        required: {
                          value: true,
                          message: "Please enter site address.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.siteAddress?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS") + t("O_ARABIC")}</>}
                      type="text"
                      id="siteAddressAr"
                      register={register("siteAddressAr", {
                        required: {
                          value: true,
                          message: "Please enter site address in arabic.",
                        },
                      })}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_2")}</>}
                      type="text"
                      id="siteAddress2"
                      register={register("siteAddress2", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_2") + t("O_ARABIC")}</>}
                      type="text"
                      id="siteAddressAr2"
                      register={register("siteAddressAr2", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_3")}</>}
                      type="text"
                      id="siteAddress3"
                      register={register("siteAddress3", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_3") + t("O_ARABIC")}</>}
                      type="text"
                      id="siteAddressAr3"
                      register={register("siteAddressAr3", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_4")}</>}
                      type="text"
                      id="siteAddress4"
                      register={register("siteAddress4", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("SITE_ADDRESS_4") + t("O_ARABIC")}</>}
                      type="text"
                      id="siteAddressAr4"
                      register={register("siteAddressAr4", {})}
                      placeholder=" "
                    />
                  </div>
                  <ErrorMessage message={errors?.siteAddressAr?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("GOOGLE_ANALYTICS_CODE")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="googleAnalyticsCode"
                      register={register("googleAnalyticsCode", {
                        required: {
                          value: true,
                          message: "Please enter google analytics code.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage
                    message={errors?.googleAnalyticsCode?.message}
                  />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("FACEBOOK")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="facebookUrl"
                      register={register("facebookUrl", {
                        required: {
                          value: true,
                          message: "Please enter facebook url.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.facebookUrl?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("LINKDIN")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="linkedinUrl"
                      register={register("linkedinUrl", {
                        required: {
                          value: true,
                          message: "Please enter linkedin url",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.linkedinUrl?.message} />
                </div>
                <div className=" w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("TWITTER")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="twitterUrl"
                      register={register("twitterUrl", {
                        required: {
                          value: true,
                          message: "Please enter twitter url.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.twitterUrl?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("INSTAGRAM")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="instgramUrl"
                      register={register("instgramUrl", {
                        required: {
                          value: true,
                          message: "Please enter instagram url.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.instgramUrl?.message} />
                </div>
                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("YOUTUBE")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="youtubeUrl"
                      register={register("youtubeUrl", {
                        required: {
                          value: true,
                          message: "Please enter youtube url.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.youtubeUrl?.message} />
                </div>

                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("CUSTOMER_ANDROID_VERSION")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="customerAndroidVersion"
                      register={register("androidVersion", {
                        required: {
                          value: true,
                          message: "Please enter Customer Android version.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.androidVersion?.message} />
                </div>

                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("CUSTOMER_IOS_VERSION")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="customeriOSVersion"
                      register={register("iOSVersion", {
                        required: {
                          value: true,
                          message: "Please enter Customer iOS VERSION.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.iOSVersion?.message} />
                </div>

                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("AGENT_ANDROID_VERSION")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="agentAndroidVersion"
                      register={register("agentAndroidVersion", {
                        required: {
                          value: true,
                          message: "Please enter Agent Android version.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage
                    message={errors?.agentAndroidVersion?.message}
                  />
                </div>

                <div className="w-full">
                  <div className="flex">
                    <OInputField
                      wrapperClassName="relative z-0 mb-6  w-full group"
                      inputLabel={<>{t("AGENT_IOS_VERSION")}</>}
                      type="text"
                      // onInput={(e) => preventMax(e)}
                      // onKeyDown={(event) => {
                      //   if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                      //     event.preventDefault()
                      //   }
                      // }}
                      id="agentiOSVersion"
                      register={register("agentiOSVersion", {
                        required: {
                          value: true,
                          message: "Please enter Agent iOS VERSION.",
                        },
                      })}
                      placeholder=" "
                    />
                    {/* <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside> */}
                  </div>
                  <ErrorMessage message={errors?.agentiOSVersion?.message} />
                </div>

                <div className="mb-4  w-full" style={{ marginLeft: "15px" }}>
                  <div>
                    {/* <div className='py-4 flex justify-center'> */}
                    <div className="relative w-24 h-24 ">
                      <label className="block text-gray-700 text-sm font-medium mb-2 ml-6">
                        Logo
                        {/* <span className='text-red-500'>*</span> */}
                      </label>
                      <input
                        type="file"
                        accept="image/png, image/jpg, image/jpeg"
                        name="image"
                        ref={profilePicRef}
                        onChange={handleFileChange}
                        className="hidden"
                      />

                      <OImage
                        src={profilePic ? URL.createObjectURL(profilePic) : pic}
                        fallbackUrl="/images/user.png"
                        className="w-24 h-24"
                        alt=""
                        style={{ borderRadius: "50%" }}
                      />
                      <AiFillCamera
                        className=" bg-gray-100  absolute w-4 rounded-xl cursor-pointer"
                        style={{
                          width: "1.5rem",
                          height: "1.5rem",
                          // bottom: '1px',
                          top: "85px",
                          right: 0,
                          background: "",
                          padding: "1px",
                        }}
                        onClick={() => profilePicRef?.current?.click()}
                      />
                    </div>
                    {/* </div> */}
                    <div className="text-center mt-4">
                      <ErrorMessage message={profilePicError} />
                    </div>
                  </div>
                </div>
                <div className="mb-4  w-full" style={{ marginLeft: "15px" }}>
                  <div>
                    {/* <div className='py-4 flex justify-center'> */}
                    <div className="relative w-24 h-24 ">
                      <label className="block text-gray-700 text-sm font-medium mb-2 ml-6">
                        Login Banner
                        {/* <span className='text-red-500'>*</span> */}
                      </label>
                      <input
                        type="file"
                        accept="image/png, image/jpg, image/jpeg"
                        name="login_image"
                        ref={loginPicRef}
                        onChange={handleBannerFileChange}
                        className="hidden"
                      />

                      <OImage
                        src={loginPic ? URL.createObjectURL(loginPic) : banner}
                        fallbackUrl="/images/uploadimg.png"
                        className="w-24 h-24"
                        alt=""
                        style={{ borderRadius: "50%" }}
                      />
                      <AiFillCamera
                        className=" bg-gray-100  absolute w-4 rounded-xl cursor-pointer"
                        style={{
                          width: "1.5rem",
                          height: "1.5rem",
                          // bottom: '1px',
                          top: "85px",
                          right: 0,
                          background: "",
                          padding: "1px",
                        }}
                        onClick={() => loginPicRef?.current?.click()}
                      />
                    </div>
                    {/* </div> */}
                    <div className="text-center mt-4">
                      <ErrorMessage message={loginPicError} />
                    </div>
                  </div>
                </div>
              </main>
            </div>
          </div>

          {/* <div className='border xl:w-full rounded mt-5'>
            <header className='border-b  py-2 px-4 bg-gray-100 rounded-t-md '>
              <div className='font-semibold'>{t('SETTINGS_COMMISSION')}</div>
            </header>
            <div className='bg-white py-6 px-4  rounded-b-md'>
              <main className='justify-center flex flex-wrap grid  lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1  gap-4'>
                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_CASH_IN_COMMISSION')}

                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='text'
                      id='cash_in_commission'
                      onInput={(e) => preventMaxHundred(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab', '.'].includes(event.key) && !/^[0-9]{1,2}(\.[0-9]{1,2})?$/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('cash_in_commission', {
                        required: {
                          value: true,
                          message: 'Please enter valid commission.'
                        },
                        pattern: {
                          value: /^[0-9]{1,2}(\.[0-9]{1,2})?$/,
                          message: 'Only digits are allowed.'
                        },
                        maxLength: 2,
                        max: {
                          value: 100,
                          message: 'Maximum value must is 100.'
                        }
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      %
                    </aside>
                  </div>
                  <ErrorMessage
                    message={errors?.cash_in_commission?.message}
                  />
                </div>
                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_CASH_OUT_COMMISSION')}

                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      id='cash_out_commission'
                      type='text'
                      onInput={(e) => preventMaxHundred(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab', '.'].includes(event.key) && !/^[0-9]{1,2}(\.[0-9]{1,2})?$/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('cash_out_commission', {
                        required: {
                          value: true,
                          message: 'Please enter cash out commission.'
                        },
                        pattern: {
                          value: /^[0-9]{1,2}(\.[0-9]{1,2})?$/,
                          message: 'Only digits are allowed.'
                        },
                        maxLength: 2,
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      %
                    </aside>
                  </div>
                  <ErrorMessage
                    message={errors?.cash_out_commission?.message}
                  />
                </div>
                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_DEALS_COMMISSION')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='text'
                      id='deals_commission'
                      onInput={(e) => preventMaxHundred(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab', '.'].includes(event.key) && !/^[0-9]{1,2}(\.[0-9]{1,2})?$/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('deals_commission', {
                        required: {
                          value: true,
                          message: 'Please enter valid commission.'
                        },
                        pattern: {
                          value: /^[0-9]{1,2}(\.[0-9]{1,2})?$/,
                          message: 'Only digits are allowed.'
                        },
                        maxLength: 2,
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      %
                    </aside>
                  </div>
                  <ErrorMessage message={errors?.deals_commission?.message} />
                </div>
              </main>
            </div>
          </div> */}

          {/* <div className='border xl:w-full rounded mt-5'>
            <header className='border-b  py-2 px-4 bg-gray-100 rounded-t-md '>
              <div className='font-semibold'>{t('SETTINGS_LOYALTY_POINTS')}</div>
            </header>
            <div className='bg-white py-6 px-4  rounded-b-md'>
              <main className='justify-center flex flex-wrap grid  lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1  gap-4'>

                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_NUMBER_OF_LOYALTY_POINTS')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='Number'
                      id='conversion_point'
                      onInput={(e) => preventMax(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('conversion_point', {
                        required: {
                          value: true,
                          message:
                            'Please enter conversion rate of loyalty point.'
                        },
                        maxLength: {
                          value: 10,
                          message: 'Max limit is 10 characters.'
                        },
                        min: {
                          value: 1,
                          message: 'Minimum value must is 1.'
                        }
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      Points
                    </aside>
                  </div>
                  <ErrorMessage message={errors?.conversion_point?.message} />
                </div>

                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_CONVERSION_RATE_OF_LOYALTY_POINT')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='number'
                      onInput={(e) => preventMax(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('conversion_rate', {
                        required: {
                          value: true,
                          message:
                            'Please enter conversion rate of loyalty point.'
                        },
                        maxLength: {
                          value: 10,
                          message: 'Max limit is 10 characters.'
                        },
                        min: {
                          value: 1,
                          message: 'Minimum value must is 1.'
                        }
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside>
                  </div>
                  <ErrorMessage message={errors?.conversion_rate?.message} />
                </div>
                <div className='mb-4 w-full'>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_UTILITY_LOYALTY_POINT')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='text'
                      id='utilityLoyaltyPoint'
                      onInput={(e) => preventMaxHundred(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab', '.'].includes(event.key) && !/^[0-9]{1,2}(\.[0-9]{1,2})?$/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('utilityLoyaltyPoint', {
                        required: {
                          value: true,
                          message: 'Please enter utility loyalty points.'
                        },
                        pattern: {
                          value: /^[0-9]{1,2}(\.[0-9]{1,2})?$/,
                          message: 'Only digits are allowed.'
                        },
                        maxLength: 2,
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      %
                    </aside>
                  </div>
                  <ErrorMessage message={errors?.utilityLoyaltyPoint?.message} />
                </div>
                <div className='mb-4   w-full  '>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_THRESHOLD_VALUE_OF_REDEEM_POINT')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='number'
                      onInput={(e) => preventMax(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab'].includes(event.key) && !/[0-9]/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('threshold_value_to_redeem_point', {
                        required: {
                          value: true,
                          message:
                            'Please enter threshold value to redeem point.'
                        },
                        maxLength: {
                          value: 10,
                          message: 'Max limit is 10 characters.'
                        },
                        min: {
                          value: 1,
                          message: 'Minimum value must is 1.'
                        }
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      LEK
                    </aside>
                  </div>
                  <ErrorMessage
                    message={errors?.threshold_value_to_redeem_point?.message}
                  />
                </div>
                <div className='mb-4 w-full'>
                  <label className='block text-gray-700 text-sm font-medium mb-2 '>
                    {t('SETTINGS_DEALS_LOYALTY_POINT')}
                  </label>
                  <div className='flex border rounded'>
                    <input
                      className='shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline'
                      type='text'
                      id='dealsLoyaltyPoint'
                      onInput={(e) => preventMaxHundred(e)}
                      onKeyDown={(event) => {
                        if (!['Backspace', 'Delete', 'Tab', '.'].includes(event.key) && !/^[0-9]{1,2}(\.[0-9]{1,2})?$/.test(event.key)) {
                          event.preventDefault()
                        }
                      }}
                      {...register('dealsLoyaltyPoint', {
                        required: {
                          value: true,
                          message: 'Please enter deals loyalty points.'
                        },
                        pattern: {
                          value: /^[0-9]{1,2}(\.[0-9]{1,2})?$/,
                          message: 'Only digits are allowed.'
                        },
                        maxLength: 2,
                      })}
                      placeholder=''
                    />
                    <aside className='text-center flex flex-col justify-center p-2 bg-gray-300 text-gray-700'>
                      %
                    </aside>
                  </div>
                  <ErrorMessage message={errors?.dealsLoyaltyPoint?.message} />
                </div>
              </main>
            </div>
          </div> */}
        </section>
        {(manager?.add || user?.role === "admin") && (
          <div className="text-center mt-4">
            <OButton
              disabled={!isDirty && isDisable}
              label={<>{t("O_UPDATE")}</>}
              type="submit"
              loading={settingChangeLoading}
            />
          </div>
        )}
      </form>
    </section>
  );
};

export default Settings;
